import type { BudgetAiExtractUnmatched } from 'services'

import type { IBudgetItem } from './types'

export type UnmatchedBudgetItem = BudgetAiExtractUnmatched

export type UnmatchedSelection = {
  sectionValue: string
  lineKey: string | null
  /** Set when the target section does not exist yet and must be created under this label. */
  newSectionLabel: string | null
}

export type UnmatchedBudgetPlan = {
  /** Select options, keyed as `line:<section>:<key>`, `new:<section>` or `newsection:<label>`. */
  options: Record<string, string>
  /** Headings the document printed that this budget has no section for, in document order. */
  newSectionLabels: string[]
  /** Pre-filled choice per unmatched row, by index. */
  selections: Record<number, string>
}

const normalize = (s: string | null | undefined) => (s || '').trim().toLowerCase()

/** Compare headings without the punctuation a form prints, so 'Soft Costs:' meets 'SOFT COSTS'. */
export const normalizeHeading = (s: string | null | undefined) =>
  (s || '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim()

/**
 * Section key for a heading the document brought with it.
 *
 * Derived from the label rather than a timestamp, so every line under the same heading lands in one
 * section instead of each opening its own.
 */
export const sectionValueForLabel = (label: string) =>
  `docSection_${normalizeHeading(label).replace(/ /g, '_') || 'section'}`

/** The budget's own section that answers to the heading the document printed, if any. */
export const findMatchingSection = (
  heading: string | null | undefined,
  sections: Array<{ value: string; label: string }>,
) => {
  const needle = normalizeHeading(heading)
  if (!needle) return null
  const exact = sections.find((s) => normalizeHeading(s.label) === needle)
  if (exact) return exact.value
  const partial = sections.find((s) => {
    const label = normalizeHeading(s.label)
    return label && (label.includes(needle) || needle.includes(label))
  })
  return partial?.value || null
}

const findBestLine = (title: string | null, options: Array<{ key: string; title: string }>) => {
  const needle = normalize(title)
  if (!needle) return null
  const exact = options.find((o) => normalize(o.title) === needle)
  if (exact) return exact.key
  const partial = options.find((o) => {
    const t = normalize(o.title)
    return t && (t.includes(needle) || needle.includes(t))
  })
  return partial?.key || null
}

/** Read a select value back into the section and line it names. */
export const parseUnmatchedSelection = (value: string): UnmatchedSelection | null => {
  if (!value) return null
  if (value.startsWith('newsection:')) {
    const label = value.slice('newsection:'.length)
    return { sectionValue: sectionValueForLabel(label), lineKey: null, newSectionLabel: label }
  }
  if (value.startsWith('new:')) {
    return { sectionValue: value.slice('new:'.length), lineKey: null, newSectionLabel: null }
  }
  if (value.startsWith('line:')) {
    const rest = value.slice('line:'.length)
    const sep = rest.indexOf(':')
    if (sep === -1) return null
    return { sectionValue: rest.slice(0, sep), lineKey: rest.slice(sep + 1), newSectionLabel: null }
  }
  return null
}

/**
 * Work out where every line from the document should go, before the user sees the screen.
 *
 * A budget kept in the lender's own format matches almost nothing on the Nexys roster, so a hundred
 * line form arrives as a hundred unmatched rows. Asking for a hundred choices is not an import. The
 * order is: an existing line by the same name, then the budget's own section for the heading the
 * document used, then that heading offered as a new section.
 */
export const buildUnmatchedBudgetPlan = (
  unmatched: UnmatchedBudgetItem[],
  budgetItems: IBudgetItem[],
  occupiedLineKeys: string[] = [],
): UnmatchedBudgetPlan => {
  const occupied = new Set(occupiedLineKeys)
  const sections = (budgetItems || []).map((s) => ({ value: s.value, label: s.label || s.value }))

  const options: Record<string, string> = {}
  const lineOptions: Array<{ key: string; title: string }> = []

  for (const section of budgetItems || []) {
    const label = section.label || section.value
    options[`new:${section.value}`] = `Add as new item under “${label}”`

    for (const lineKey of Object.keys(section.data || {})) {
      if (occupied.has(`${section.value}::${lineKey}`)) continue
      const title = section.data[lineKey]?.title || lineKey
      const key = `line:${section.value}:${lineKey}`
      options[key] = `${label} › ${title}`
      lineOptions.push({ key, title })
    }
  }

  const newSectionLabels: string[] = []
  for (const item of unmatched) {
    const heading = (item.section || '').trim()
    if (!heading) continue
    if (findMatchingSection(heading, sections)) continue
    if (newSectionLabels.some((l) => normalizeHeading(l) === normalizeHeading(heading))) continue
    newSectionLabels.push(heading)
  }

  // A form organised into its own sections has nowhere to land on a budget named differently.
  // Offering its headings as new sections is what lets a whole form import in one pass.
  for (const label of newSectionLabels) {
    options[`newsection:${label}`] = `Add under a NEW section “${label}” (from the document)`
  }

  const selections: Record<number, string> = {}
  const takenLines = new Set<string>()
  unmatched.forEach((item, idx) => {
    const line = findBestLine(item.title, lineOptions)
    // Two document lines can read as the same roster line; the screen refuses duplicates, so only
    // the first claims it and the rest fall through to their section.
    if (line && !takenLines.has(line)) {
      takenLines.add(line)
      selections[idx] = line
      return
    }
    const existingSection = findMatchingSection(item.section, sections)
    if (existingSection) {
      selections[idx] = `new:${existingSection}`
      return
    }
    const heading = (item.section || '').trim()
    if (heading && options[`newsection:${heading}`]) selections[idx] = `newsection:${heading}`
  })

  return { options, newSectionLabels, selections }
}
